import * as React from 'react';
import {createViewConfig, view} from '@sail/engine';
import type {View} from '@sail/engine';
import {useCollectionState} from '@sail/react-aria';
import {message, useIntl} from '@sail/intl';
import type {NativeCheckboxProps} from './Checkbox';
import {Checkbox} from './Checkbox';
import useFauxTreeState from '../hooks/useFauxTreeState';
import type {FormFieldProps} from './FormField';
import {css} from '../css';

const selectAllMessage = message({
  id: '@sail/ui.fauxTreeSelectAllItem.selectAll',
  description: 'Label for the select all checkbox when no items are selected',
  defaultMessage: 'Select all',
});

const deselectAllMessage = message({
  id: '@sail/ui.fauxTreeSelectAllItem.deselectAll',
  description: 'Label for the select all checkbox when all items are selected',
  defaultMessage: 'Deselect all',
});

const selectedCountMessage = message<{count: number}>({
  id: '@sail/ui.fauxTreeSelectAllItem.selectedCount',
  description:
    'Label for the select all checkbox showing how many items are selected',
  defaultMessage: '{count} selected',
});

export type FauxTreeSelectAllItemProps = View.IntrinsicElement<
  'div',
  {
    selectedText?: (itemsCount: number, allCount: number) => string;
    subviews?: FormFieldProps<NativeCheckboxProps>['subviews'];
    selectAllRef?: React.MutableRefObject<HTMLInputElement | null>;
  }
>;

export const FauxTreeSelectAllItemConfig = createViewConfig({
  name: 'FauxTreeSelectAllItem',
  props: {} as FauxTreeSelectAllItemProps,
});

export const FauxTreeSelectAllItem = FauxTreeSelectAllItemConfig.createView(
  (allProps) => {
    const {subviews, selectedText, selectAllRef, ...props} = allProps;
    const {formatMessage} = useIntl();

    const state = useCollectionState();

    const {selectAllItem, selectGroupItems} = useFauxTreeState();

    const {
      selectedCount,
      totalCount,
      selectedHiddenCount,
      checkableKeys,
      uncheckableKeys,
      isDisabled,
    } = selectAllItem;

    const areAllSelected = totalCount === selectedCount;
    const areAllEnabledSelected =
      checkableKeys.length - selectedHiddenCount === selectedCount;

    // TODO: Sail Checkbox has a visual bug in Chrome when the values of indeterminate and checked are modified in the same render.
    // This is a workaround to avoid it, but this bug must be fixed directly in Checkbox
    const [indeterminate, setIndeterminate] = React.useState(
      selectedCount !== 0 && !areAllSelected,
    );

    React.useEffect(() => {
      setIndeterminate(selectedCount !== 0 && !areAllSelected);
    }, [selectedCount, areAllSelected]);

    const selectionLabel = React.useMemo(() => {
      if (selectedText) {
        return selectedText(selectedCount, totalCount);
      }

      if (selectedCount === 0) {
        return formatMessage(selectAllMessage);
      }

      if (selectedCount === totalCount) {
        return formatMessage(deselectAllMessage);
      }

      return formatMessage(selectedCountMessage, {count: selectedCount});
    }, [selectedText, selectedCount, totalCount, formatMessage]);

    const handleToggleSelectAll = React.useCallback(() => {
      if (isDisabled) {
        return;
      }
      if (areAllEnabledSelected) {
        state.selectionManager.setSelectedKeys(uncheckableKeys);
      } else {
        state.selectionManager.setSelectedKeys(checkableKeys);
      }
    }, [
      isDisabled,
      areAllEnabledSelected,
      state.selectionManager,
      checkableKeys,
      uncheckableKeys,
    ]);

    return (
      <view.div
        {...props}
        onClick={(e: React.MouseEvent) => {
          // Only handle clicks that originate from this div itself (the padding/gap area),
          // not clicks that bubble up from the checkbox or label inside.
          if (e.target === e.currentTarget) {
            handleToggleSelectAll();
          }
        }}
        css={[
          selectGroupItems.size > 0
            ? css({marginBottom: 'xsmall'})
            : css({marginBottom: 'space.0'}),
          !isDisabled ? css({cursor: 'pointer'}) : css({cursor: 'default'}),
        ]}
      >
        <Checkbox
          tabIndex={-1}
          {...(selectAllRef ? {ref: selectAllRef} : {})}
          indeterminate={indeterminate}
          label={selectionLabel}
          checked={areAllSelected}
          disabled={isDisabled}
          subviews={{
            label: {
              css: [
                css({
                  marginLeft: '-2px',
                  font: 'label.small.emphasized',
                  color: 'subdued',
                }),
                !isDisabled
                  ? css({cursor: 'pointer'})
                  : css({cursor: 'default'}),
              ],
              // @ts-expect-error - Error after migration to React 18
              ...subviews?.label,
            },
            input: {
              css: {
                ':focus': {
                  focusRing: 'none',
                },
              },
              // @ts-expect-error - Error after migration to React 18
              ...subviews?.input,
            },
            ...subviews,
          }}
          onChange={handleToggleSelectAll}
        />
      </view.div>
    );
  },
  {
    css: {
      stack: 'x',
      font: 'label.small.emphasized',
      color: 'subdued',
      gap: 'space.75',
      borderRadius: 'xsmall',
      paddingY: 'space.75',
      paddingX: 'space.125',
      alignX: 'start',
      alignY: 'center',
      minHeight: 'xlarge',
    },
  },
);
